import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import KBLogo from './KBLogo';
import axios from 'axios';
// ==================== PRELOADER COMPONENT ====================
const Preloader = ({ onComplete }) => {
  return (
    <motion.div
      className="fixed inset-0 z-[999] flex items-center justify-center bg-charcoal"
      initial={{ opacity: 1 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Wrapper to ensure everything stays centered as a single unit */}
      <div className="flex flex-col items-center justify-center gap-4 md:gap-8">
        <motion.div
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        >
          <KBLogo size={80} animate />
        </motion.div>
        
        <motion.h1
          className="text-6xl md:text-8xl font-black text-emerald-accent tracking-widest text-center leading-none"
          initial={{ filter: 'blur(20px)', scale: 0.8, opacity: 0 }}
          animate={{ filter: 'blur(0px)', scale: 1, opacity: 1 }}
          transition={{ duration: 0.8, ease: 'easeOut', delay: 0.2 }}
          onAnimationComplete={() => setTimeout(onComplete, 500)}
        >
          KSHITIZ
        </motion.h1>
      </div>
    </motion.div>
  );
};
// ==================== HERO SECTION ====================
const HeroSection = () => {
  const titles = ['The Influencer', 'The Fitness Guru', 'The Visionary'];
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % titles.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [titles.length]);

  return (
    <section className="min-h-screen flex flex-col items-center justify-center px-4 py-20">
      {/* Dynamic Headline */}
      <div className="text-center mb-12">
        <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-4">
          Kshitiz
        </h1>
        <div className="h-16 md:h-20 overflow-hidden">
          <AnimatePresence mode="wait">
            <motion.p
              key={currentIndex}
              className="text-2xl md:text-4xl lg:text-5xl font-semibold text-emerald-accent"
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -50, opacity: 0 }}
              transition={{ duration: 0.5, ease: 'easeInOut' }}
            >
              "{titles[currentIndex]}"
            </motion.p>
          </AnimatePresence>
        </div>
      </div>

      {/* Central Image Placeholder */}
      <motion.div
        className="w-72 h-72 md:w-[500px] md:h-[500px] bg-dark-grey rounded-2xl overflow-hidden animate-pulse-glow"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.3 }}
      >
        <img src="/cover.jpeg" alt="Kshitij" className="w-full h-full object-cover" />
      </motion.div>

      {/* Bio Section */}
      <motion.div
        className="mt-12 max-w-3xl text-center px-4"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8, duration: 0.6 }}
      >
        <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
          I am <span className="text-emerald-accent">Kshitiz Bisht</span>
        </h2>
        <p className="text-zinc-300 text-lg md:text-xl leading-relaxed">
          I spent <span className="text-emerald-accent font-bold">10 years</span> chasing the '<span className="text-emerald-accent font-semibold">cover model</span>' look the wrong way—wasting time on cookie-cutter coaching and fighting my own biology. In <span className="text-emerald-accent font-bold">2015</span>, I stopped guessing and started decoding. By engineering a genetic-first approach, I've transformed my own physique and <span className="text-emerald-accent font-bold">500+</span> others globally. I'm not here to give you another workout plan; I'm here to give you the DNA-based blueprint to build a model-like physique without the struggle.
        </p>
      </motion.div>

      {/* Trust Line */}
      <motion.p
        className="mt-10 text-zinc-400 text-lg md:text-xl"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
      >
        Join <span className="text-emerald-accent font-bold">500+</span> Students
      </motion.p>
    </section>
  );
};

// ==================== FEATURED VIDEO (YOUTUBE) ====================
const FeaturedVideo = () => {
  // Replace 'VIDEO_ID' with the actual ID from your YouTube link
  // Example: https://www.youtube.com/watch?v=dQw4w9WgXcQ -> ID is dQw4w9WgXcQ
  const videoId = "q_3xxdOVJjU"; 
  
  return (
    <section className="py-12 px-4 max-w-6xl mx-auto">
      <motion.div 
        className="relative aspect-video rounded-3xl overflow-hidden shadow-[0_0_50px_rgba(80,200,120,0.2)] border border-emerald-accent/20 bg-dark-grey"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
      >
        <iframe
          className="w-full h-full"
          src={`https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}&rel=0`}
          title="Kshitiz Fitness Featured Video"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        ></iframe>
      </motion.div>
      
      <div className="mt-6 text-center">
        <p className="text-zinc-400 italic text-sm md:text-base">
          "The science behind the physique—watch how we do it."
        </p>
      </div>
    </section>
  );
};

// ==================== MARQUEE SECTION ====================
const MarqueeSection = () => {
  const punchlines = [
    'BE THE BEST VERSION',
    'NO EXCUSES ONLY RESULTS',
    'TRANSFORM YOUR BODY',
    'ELITE FITNESS AWAITS',
    'PUSH YOUR LIMITS',
  ];

  return (
    <section className="py-16 overflow-hidden">
      <div className="relative flex">
        <div className="flex animate-marquee whitespace-nowrap">
          {[...punchlines, ...punchlines].map((text, i) => (
            <span
              key={i}
              className="text-5xl md:text-7xl lg:text-8xl font-black mx-8 text-outline"
            >
              {text}
            </span>
          ))}
        </div>
        <div className="flex animate-marquee whitespace-nowrap absolute top-0">
          {[...punchlines, ...punchlines].map((text, i) => (
            <span
              key={i}
              className="text-5xl md:text-7xl lg:text-8xl font-black mx-8 text-outline"
            >
              {text}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};

// ==================== VIDEO CAROUSEL ====================
const VideoCarousel = () => {
  const navigate = useNavigate();
  const [selectedVideo, setSelectedVideo] = useState(null);
  
  // Add your video sources here
  const videos = [
    { src: '/assets/test1.mp4', thumbnail: '/assets/thumb1.png' },
    { src: '/assets/test2.mp4', thumbnail: '/assets/thumb2.png' },
    { src: '/assets/test3.mp4', thumbnail: '/assets/thumb3.png' },
  ];

  return (
    <>
      <section className="py-16 px-4 overflow-hidden">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-10">
          Video <span className="text-emerald-accent">Testimonials</span>
        </h2>
        <div className="relative">
          <motion.div 
            className="flex gap-6"
            animate={{ x: [0, -1800] }}
            transition={{ 
              x: { 
                duration: 30, 
                repeat: Infinity, 
                ease: "linear",
                repeatType: "loop"
              }
            }}
          >
            {[...videos, ...videos, ...videos].map((video, i) => (
              <motion.div
                key={i}
                className="flex-shrink-0 w-[300px] aspect-video bg-dark-grey rounded-xl overflow-hidden cursor-pointer hover:scale-105 transition-transform relative"
                onClick={() => setSelectedVideo(i % videos.length)}
                whileHover={{ scale: 1.05 }}
              >
                {/* Video Thumbnail */}
                {video.thumbnail ? (
                  <img 
                    src={video.thumbnail} 
                    alt={`Testimonial ${(i % videos.length) + 1}`} 
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-full bg-zinc-800" />
                )}
                {/* Play Button Overlay */}
                <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                  <div className="w-16 h-16 rounded-full bg-emerald-accent/20 flex items-center justify-center backdrop-blur-sm">
                    <svg
                      className="w-8 h-8 text-emerald-accent"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M6.3 2.841A1.5 1.5 0 004 4.11v11.78a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                    </svg>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Video Modal */}
      <AnimatePresence>
        {selectedVideo !== null && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedVideo(null)}
          >
            {/* Blurred Background */}
            <div className="absolute inset-0 bg-charcoal/80 backdrop-blur-md" />
            
            {/* Modal Content */}
            <motion.div
              className="relative z-10 w-full max-w-3xl"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                className="absolute -top-12 right-0 text-white hover:text-emerald-accent transition-colors"
                onClick={() => setSelectedVideo(null)}
              >
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              
              {/* Video Player */}
              <div className="aspect-video bg-dark-grey rounded-2xl overflow-hidden shadow-[0_0_50px_rgba(80,200,120,0.3)]">
                <video
                  src={videos[selectedVideo]?.src}
                  controls
                  autoPlay
                  className="w-full h-full object-cover"
                >
                  Your browser does not support the video tag.
                </video>
              </div>
              
              {/* Join Now Button */}
              <motion.button
                className="mt-6 w-full py-4 bg-gradient-to-r from-emerald-accent to-green-600 text-charcoal font-bold text-lg rounded-full shadow-[0_0_30px_rgba(80,200,120,0.5)]"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate('/join')}
              >
                JOIN NOW
              </motion.button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// ==================== COMMENT CAROUSEL ====================
const CommentCarousel = () => {
  const navigate = useNavigate();
  const [selectedReview, setSelectedReview] = useState(null);
  
  // Add your student screenshot images here
  const comments = [
    { name: 'Aaryan', text: 'Ab pata chala why I am naturally a night owl aur kyon early morning workout mere liye bekar hai. Time waste karna band! Thank you for the clarity!', image: '/assets/review1.png' },
    { name: 'Abhishek', text: 'Mujhe laga tha I was doing everything right, but I was stressing my body unnecessarily! Workshop ne exactly bataya what to change. A big relief!', image: '/assets/review2.png' },
    { name: 'Amit ', text: 'Jo knowledge mili hai, that is priceless. Ab pata chala kyon supplement mere pe kaam nahi karta tha. No more guesswork!', image: '/assets/review3.png' },
    { name: 'Ayush K.', text: 'Pehle har naya trend follow karta tha. Ab I know my trend! Feeling empowered. Thanks, Gene Team!', image: '/assets/review4.png' }
  ];

  return (
    <section className="py-16 px-4 overflow-hidden">
      <h2 className="text-3xl md:text-4xl font-bold text-center mb-10">
        What <span className="text-emerald-accent">Students Say</span>
      </h2>
      <div className="relative h-[600px] overflow-hidden">
        <div className="animate-scroll">
          {[...comments, ...comments].map((comment, i) => (
            <motion.div
              key={i}
              className="bg-dark-grey p-8 rounded-xl mb-6 mx-auto max-w-lg cursor-pointer"
              initial={{ opacity: 0.5 }}
              whileHover={{ scale: 1.02, opacity: 1 }}
              onClick={() => setSelectedReview(comment)}
            >
              {/* Screenshot Image Placeholder */}
              <div className="w-full h-64 bg-zinc-800 rounded-lg mb-4 flex items-center justify-center overflow-hidden">
                {comment.image ? (
                  <img src={comment.image} alt={`Review by ${comment.name}`} className="w-full h-full object-cover" loading="lazy" />
                ) : (
                  <div className="text-center">
                    <svg className="w-12 h-12 text-zinc-600 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span className="text-zinc-600 text-sm">Screenshot</span>
                  </div>
                )}
              </div>
              <p className="text-zinc-300 mb-3 text-lg">"{comment.text}"</p>
              <p className="text-emerald-accent font-semibold text-lg">{comment.name}</p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Modal Overlay */}
      <AnimatePresence>
        {selectedReview && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedReview(null)}
          >
            {/* Blurred Background */}
            <div className="absolute inset-0 bg-black/70 backdrop-blur-md" />
            
            {/* Modal Content */}
            <motion.div
              className="relative bg-dark-grey p-8 md:p-10 rounded-2xl max-w-xl w-full mx-4 z-10"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                className="absolute top-4 right-4 text-zinc-400 hover:text-white transition-colors"
                onClick={() => setSelectedReview(null)}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              {/* Review Image */}
              <div className="w-full h-72 md:h-80 bg-zinc-800 rounded-xl mb-6 flex items-center justify-center overflow-hidden">
                {selectedReview.image ? (
                  <img src={selectedReview.image} alt={`Review by ${selectedReview.name}`} className="w-full h-full object-cover" />
                ) : (
                  <div className="text-center">
                    <svg className="w-16 h-16 text-zinc-600 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span className="text-zinc-600">Screenshot</span>
                  </div>
                )}
              </div>

              {/* Review Text */}
              <p className="text-zinc-300 mb-4 text-xl">"{selectedReview.text}"</p>
              <p className="text-emerald-accent font-semibold text-xl mb-8">{selectedReview.name}</p>

              {/* Join Now Button */}
              <motion.button
                className="w-full bg-emerald-accent text-charcoal font-bold py-4 px-8 rounded-full text-lg hover:bg-emerald-400 transition-colors"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate('/join')}
              >
                JOIN NOW
              </motion.button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

const TransformationSection = () => {
  const transformations = [
    { before: '/assets/before.jpeg', after: '/assets/trans.jpeg' },
    { before: '/assets/before1.jpeg', after: '/assets/trans1.jpeg' },
    { before: '/assets/before2.jpeg', after: '/assets/trans2.jpeg' },
    { before: '/assets/before3.jpeg', after: '/assets/trans3.jpeg' },
    { before: '/assets/before4.jpeg', after: '/assets/trans4.jpeg' }
  ];

  return (
    <section className="py-20 px-4 bg-charcoal">
      <motion.h2
        className="text-4xl md:text-6xl font-black text-center mb-4 text-white uppercase tracking-tighter"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
      >
        YOUR TURN IS <span className="text-emerald-accent">NEXT</span>
      </motion.h2>
      <p className="text-zinc-400 text-center mb-16 text-lg md:text-xl">Real Results from Real People</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
        {transformations.map((transformation, i) => (
          <motion.div
            key={i}
            className="bg-dark-grey rounded-2xl overflow-hidden border border-zinc-800 shadow-2xl"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ delay: i * 0.1 }}
            viewport={{ once: true }}
          >
            <div className="flex flex-row h-full">
              {/* Before Image */}
              <div className="relative flex-1 aspect-[4/5] bg-zinc-900 border-r border-zinc-800">
                <img 
                  src={transformation.before} 
                  alt="Before" 
                  className="w-full h-full object-cover" 
                />
                <span className="absolute bottom-3 left-3 bg-black/80 text-white text-[10px] md:text-xs font-bold px-2 py-1 rounded-md uppercase tracking-wider">Before</span>
              </div>
              {/* After Image */}
              <div className="relative flex-1 aspect-[4/5] bg-zinc-900">
                <img 
                  src={transformation.after} 
                  alt="After" 
                  className="w-full h-full object-cover" 
                />
                <span className="absolute bottom-3 left-3 bg-emerald-accent text-charcoal text-[10px] md:text-xs font-bold px-2 py-1 rounded-md uppercase tracking-wider">After</span>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

// ==================== FAQ SECTION ====================
const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: "What Will I Get in Webinar?",
      answer: "Your Complete Gene-Based Roadmap to a Model Physique. Efficient, Proven, and Results-Focused System. Proven strategies that worked for me and have worked for 800+ clients worldwide."
    },
    {
      question: "Do I Need Technical Knowledge?",
      answer: "This program is designed to be incredibly easy to understand. No complex technical knowledge required—just a willingness to learn and apply."
    },
    {
      question: "Is This a Course?",
      answer: "No. It’s a personalized genetic blueprint designed to help you understand your DNA and use it to build a model-level body."
    }
  ];

  return (
    <section className="py-24 px-4 bg-charcoal">
      <div className="max-w-3xl mx-auto">
        <h2 className="text-4xl md:text-5xl font-bold text-center mb-4 text-white">
          Frequently Asked <span className="text-emerald-accent">Questions</span>
        </h2>
        <p className="text-zinc-500 text-center mb-16 text-lg">Got questions? We've got answers.</p>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div key={index} className="border border-zinc-800 rounded-2xl bg-dark-grey/50 overflow-hidden">
              <button
                className="w-full p-6 text-left flex items-center justify-between gap-4 transition-all hover:bg-zinc-800/30"
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
              >
                <span className="text-lg font-bold text-white leading-tight">{faq.question}</span>
                <span className={`text-emerald-accent text-2xl transition-transform duration-300 ${openIndex === index ? 'rotate-45' : 'rotate-0'}`}>
                  +
                </span>
              </button>
              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <p className="px-6 pb-6 text-zinc-400 text-base leading-relaxed border-t border-zinc-800/50 pt-4">
                      {faq.answer}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
// ==================== CTA SECTION ====================
const CTASection = () => {
  const navigate = useNavigate();
  const currentMonth = new Date().toLocaleString('default', { month: 'long' });
  const [timeLeft, setTimeLeft] = useState(30 * 60);

  useEffect(() => {
    const timer = setInterval(() => setTimeLeft((prev) => (prev > 0 ? prev - 1 : 0)), 1000);
    return () => clearInterval(timer);
  }, []);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <section className="py-24 px-4 text-center bg-charcoal">
      <div className="max-w-4xl mx-auto flex flex-col items-center">
        <h2 className="text-4xl md:text-6xl font-black mb-6 text-white leading-tight">
          READY TO <span className="text-emerald-accent">TRANSFORM</span>?
        </h2>
        <p className="text-zinc-400 mb-2 text-xl font-medium">Join 500+ successful students</p>
        <p className="text-yellow-500 mb-10 font-bold tracking-wide uppercase">⚡ Limited Slots for {currentMonth}</p>

        <div className="mb-12 bg-dark-grey border border-emerald-accent/20 rounded-3xl p-8 shadow-2xl">
          <p className="text-zinc-500 text-sm mb-4 uppercase tracking-widest font-bold">Special offer ends in</p>
          <div className="flex flex-col items-center">
            <span className="text-5xl md:text-6xl font-black text-emerald-accent font-mono mb-2">
              {formatTime(timeLeft)}
            </span>
            <div className="h-1 w-20 bg-emerald-accent/20 rounded-full"></div>
            <p className="text-[10px] text-zinc-600 mt-3 font-bold uppercase tracking-tighter">Minutes : Seconds</p>
          </div>
        </div>

        <motion.button
          className="px-16 py-5 bg-emerald-accent text-charcoal font-black text-xl rounded-full shadow-[0_0_40px_rgba(80,200,120,0.4)] transition-all"
          whileHover={{ scale: 1.05, boxShadow: "0_0_60px_rgba(80,200,120,0.6)" }}
          whileTap={{ scale: 0.95 }}
          onClick={() => navigate('/join')}
        >
          JOIN THE COMMUNITY NOW
        </motion.button>
      </div>
    </section>
  );
};
// ==================== STICKY BOTTOM CTA ====================
const StickyBottomCTA = () => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsVisible(window.scrollY > 500);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          className="fixed bottom-0 left-0 right-0 p-4 bg-charcoal/90 backdrop-blur-md border-t border-zinc-800 md:hidden z-40"
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
        >
          <motion.button
            className="w-full py-4 bg-gradient-to-r from-emerald-accent to-green-600 text-charcoal font-bold text-lg rounded-full shadow-[0_10px_40px_rgba(80,200,120,0.4)]"
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate('/join')}
          >
            JOIN NOW - Limited Slots!
          </motion.button>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// ==================== FOOTER SECTION ====================
const Footer = () => {
  return (
    <footer className="py-12 px-4 border-t border-zinc-800 bg-dark-grey/30">
      <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-center gap-8">
        <div className="text-center md:text-left">
          <KBLogo size={40} />
          <p className="mt-4 text-zinc-500 text-sm max-w-xs">
            Decoding fitness through genetics to build your ultimate model physique.
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-6 text-sm font-medium text-zinc-400">
          <a href="/privacy-policy" className="hover:text-emerald-accent transition-colors">Privacy Policy</a>
          <a href="/terms" className="hover:text-emerald-accent transition-colors">Terms & Conditions</a>
          <a href="/refund-policy" className="hover:text-emerald-accent transition-colors">Refund Policy</a>
          <a href="/contact" className="hover:text-emerald-accent transition-colors">Contact Us</a>
        </div>
      </div>
      
      <div className="mt-12 text-center text-xs text-zinc-600 border-t border-zinc-800/50 pt-8">
        <p>© {new Date().getFullYear()} Kshitiz Bisht Fitness. All Rights Reserved.</p>
      </div>
    </footer>
  );
};

// ==================== MAIN LANDING PAGE ====================
const LandingPage = () => {
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const wakeUpBackend = async () => {
      try {
        const API_BASE = process.env.REACT_APP_BACKEND_URL || "https://fitnessfreak-44cu.onrender.com";
        await axios.get(`${API_BASE}/api/settings`);
      } catch (err) {
        console.error("Backend warm-up failed", err);
      }
    };

    if (!loading) {
      wakeUpBackend();
      if (window.fbq) {
        window.fbq('track', 'ViewContent');
      }
    }
  }, [loading]);
  
  return (
    <div className="bg-charcoal min-h-screen">
      <AnimatePresence>
        {loading && <Preloader onComplete={() => setLoading(false)} />}
      </AnimatePresence>

      {!loading && (
        <motion.main
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <header className="fixed top-0 left-0 right-0 z-40 p-4 md:p-6">
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <KBLogo size={50} />
            </motion.div>
          </header>

          <HeroSection />
          <FeaturedVideo />
          <MarqueeSection />
          <VideoCarousel />
          <CommentCarousel />
          <TransformationSection />
          <FAQSection />
          <CTASection />
          <Footer /> {/* 🟢 Added the Footer here */}
          <StickyBottomCTA />
          
          <div className="h-20 md:hidden" />
        </motion.main>
      )}
    </div>
  );
};

export default LandingPage;

